import api from "../index";

// ** Type Definitions ** //

/**
 * Report types for pulse reports
 */
export type ReportType =
  | "invoice"
  | "usage"
  | "performance"
  | "analytics"
  | "digest"
  | "newsletter"
  | "product_update"
  | "custom";

/**
 * Schedule types for pulse reports
 */
export type ScheduleType =
  | "on_refill"
  | "weekly"
  | "monthly"
  | "quarterly"
  | "custom_cron"
  | "manual";

/**
 * Pulse report status
 */
export type PulseReportStatus = "draft" | "active" | "paused" | "sending" | "archived" | "failed";

/**
 * Target type for pulse reports
 */
export type TargetType = "all" | "planBased" | "targeted" | "dateRange" | "external";

/**
 * Schedule configuration
 */
export interface ScheduleConfig {
  type: ScheduleType;
  delayAfterRefill?: number;
  dayOfWeek?: number;
  dayOfMonth?: number;
  quarterlyMonths?: number[];
  cronExpression?: string;
  timeOfDay?: {
    hour: number;
    minute: number;
  };
  timezone?: string;
}

/**
 * Data sections configuration
 */
export interface DataSections {
  billing?: {
    enabled: boolean;
    includeFields?: Record<string, boolean>;
  };
  usage?: {
    enabled: boolean;
    includeFields?: Record<string, boolean>;
  };
  performance?: {
    enabled: boolean;
    includeFields?: Record<string, boolean>;
  };
  analytics?: {
    enabled: boolean;
    includeFields?: Record<string, boolean>;
  };
  team?: {
    enabled: boolean;
    includeFields?: Record<string, boolean>;
  };
}

/**
 * Email settings configuration
 */
export interface EmailSettings {
  subject: string;
  fromName?: string;
  replyTo?: string;
  emailsToUse?: Array<{
    email: string;
    useAlias?: boolean;
    selectedAlias?: string;
  }>;
  unsubscribeLink?: string;
  sendRate?: number;
  batchSize?: number;
}

/**
 * Pulse report statistics
 */
export interface PulseReportStatistics {
  totalSent: number;
  totalDelivered: number;
  totalOpened: number;
  totalClicked: number;
  totalFailed: number;
  totalBounced: number;
  openRate: number;
  clickRate: number;
  lastRunAt?: string;
  lastRunStats?: {
    sent: number;
    failed: number;
    duration: number;
    targetedUsers: number;
  };
  nextRunAt?: string;
  runCount: number;
  // Recovery system fields
  lastHeartbeat?: string;
  lastCheckpoint?: {
    index: number;
    timestamp: string;
    processedCount: number;
    remainingCount: number;
    runId: string;
  };
  pausedAt?: string;
  pauseReason?: string;
  recoveredAt?: string;
  recoveryAttempts?: number;
}

/**
 * Pulse report progress tracking (for sending status)
 */
export interface PulseReportProgress {
  reportId: string;
  name: string;
  status: string;
  progress: {
    sent: number;
    delivered: number;
    failed: number;
    total: number;
    percentage: number;
  };
  sendingRate: {
    configured: number;
    current: number;
    totalSenders: number;
    effectiveRate: number;
  };
  timing: {
    startedAt: string | null;
    estimatedTimeRemaining: number | null;
    estimatedCompletionTime: string | null;
  };
  checkpoint?: {
    index: number;
    processedCount: number;
    remainingCount: number;
    runId: string;
  };
  recentActivity: {
    lastFiveMinutes: number;
  };
  timestamp: string;
}

/**
 * Pulse report data structure
 */
export interface PulseReport {
  _id: string;
  name: string;
  description?: string;
  reportType: ReportType;
  htmlTemplate: string | { _id: string; name: string };
  targetType: TargetType;
  includedPlans?: string[];
  excludedUsers?: string[];
  targetedUsers?: string[];
  dateRangeFilter?: {
    startDate?: string;
    endDate?: string;
  };
  externalRecipients?: Array<{
    email: string;
    values?: Record<string, string>;
  }>;
  schedule: ScheduleConfig;
  dataSections: DataSections;
  emailSettings: EmailSettings;
  variables?: Array<{
    key: string;
    defaultValue: string;
    description?: string;
  }>;
  status: PulseReportStatus;
  statistics: PulseReportStatistics;
  activityLog?: Array<{
    action: string;
    timestamp: string;
    performedBy?: string;
    details?: Record<string, any>;
  }>;
  createdAt: string;
  updatedAt: string;
}

/**
 * Pagination data
 */
export interface PaginationData {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

/**
 * Get pulse reports response
 */
export interface GetPulseReportsResponse {
  status: string;
  data: PulseReport[];
  pagination: PaginationData;
}

/**
 * Get pulse report by ID response
 */
export interface GetPulseReportResponse {
  status: string;
  data: PulseReport;
}

/**
 * Create/Update pulse report request
 */
export interface PulseReportRequest {
  name: string;
  description?: string;
  reportType: ReportType;
  htmlTemplate: string;
  targetType?: TargetType;
  includedPlans?: string[];
  excludedUsers?: string[];
  targetedUsers?: string[];
  dateRangeFilter?: {
    startDate?: string;
    endDate?: string;
  };
  externalRecipients?: Array<{
    email: string;
    values?: Record<string, string>;
  }>;
  schedule: ScheduleConfig;
  dataSections?: DataSections;
  emailSettings: EmailSettings;
  variables?: Array<{
    key: string;
    defaultValue: string;
    description?: string;
  }>;
}

/**
 * Send test request
 */
export interface SendTestRequest {
  testEmails: string[];
  useUserId?: string;
}

/**
 * Statistics response
 */
export interface StatisticsResponse {
  status: string;
  data: {
    overview: {
      totalSent: number;
      totalDelivered: number;
      totalOpened: number;
      totalClicked: number;
      totalFailed: number;
      totalBounced: number;
      openRate: number;
      clickRate: number;
      bounceRate: number;
    };
    byPlan: Record<string, { sent: number; opened: number; openRate: number }>;
    timeline: Array<{ date: string; sent: number; opened: number }>;
  };
}

/**
 * Available variables response
 */
export interface AvailableVariablesResponse {
  status: string;
  data: Array<{
    category: string;
    variables: Array<{
      key: string;
      description: string;
      example: string;
    }>;
  }>;
}

// ** API Functions ** //

/**
 * Get all pulse reports with pagination
 */
export const getPulseReports = async (
  page: number = 1,
  limit: number = 20,
  status?: PulseReportStatus,
  reportType?: ReportType,
  searchText?: string
): Promise<GetPulseReportsResponse> => {
  const params = new URLSearchParams({
    page: String(page),
    limit: String(limit),
  });

  if (status) params.append("status", status);
  if (reportType) params.append("reportType", reportType);
  if (searchText) params.append("searchText", searchText);

  return api.get(`/pulse-reports?${params.toString()}`);
};

/**
 * Get pulse report by ID
 */
export const getPulseReportById = async (
  id: string
): Promise<GetPulseReportResponse> => {
  return api.get(`/pulse-reports/${id}`);
};

/**
 * Create a new pulse report
 */
export const createPulseReport = async (
  data: PulseReportRequest
): Promise<{ status: string; message: string; data: PulseReport }> => {
  return api.post("/pulse-reports", data);
};

/**
 * Update a pulse report
 */
export const updatePulseReport = async (
  id: string,
  data: Partial<PulseReportRequest>
): Promise<{ status: string; message: string; data: PulseReport }> => {
  return api.put(`/pulse-reports/${id}`, data);
};

/**
 * Delete a pulse report
 */
export const deletePulseReport = async (
  id: string
): Promise<{ status: string; message: string }> => {
  return api.delete(`/pulse-reports/${id}`);
};

/**
 * Activate a pulse report
 */
export const activatePulseReport = async (
  id: string
): Promise<{ status: string; message: string; data: { status: string; nextRunAt?: string } }> => {
  return api.post(`/pulse-reports/${id}/activate`);
};

/**
 * Pause a pulse report
 */
export const pausePulseReport = async (
  id: string
): Promise<{ status: string; message: string; data: { status: string } }> => {
  return api.post(`/pulse-reports/${id}/pause`);
};

/**
 * Resume a pulse report
 */
export const resumePulseReport = async (
  id: string
): Promise<{ status: string; message: string; data: { status: string; nextRunAt?: string } }> => {
  return api.post(`/pulse-reports/${id}/resume`);
};

/**
 * Archive a pulse report
 */
export const archivePulseReport = async (
  id: string
): Promise<{ status: string; message: string; data: { status: string } }> => {
  return api.post(`/pulse-reports/${id}/archive`);
};

/**
 * Send pulse report now
 */
export const sendPulseReportNow = async (
  id: string,
  targetUsers?: string[],
  dryRun?: boolean
): Promise<{ status: string; message: string; data: any }> => {
  return api.post(`/pulse-reports/${id}/send-now`, { targetUsers, dryRun });
};

/**
 * Send test pulse report
 */
export const sendTestPulseReport = async (
  id: string,
  data: SendTestRequest
): Promise<{ status: string; message: string; data: any }> => {
  return api.post(`/pulse-reports/${id}/send-test`, data);
};

/**
 * Preview pulse report
 */
export const previewPulseReport = async (
  id: string,
  useUserId?: string
): Promise<{ status: string; data: { html: string; subject: string; variables: Record<string, any> } }> => {
  return api.post(`/pulse-reports/${id}/preview`, { useUserId });
};

/**
 * Get target count for a pulse report
 */
export const getTargetCount = async (
  id: string
): Promise<{ status: string; data: { targetCount: number } }> => {
  return api.get(`/pulse-reports/${id}/target-count`);
};

/**
 * Get pulse report statistics
 */
export const getPulseReportStatistics = async (
  id: string,
  period: string = "30d"
): Promise<StatisticsResponse> => {
  return api.get(`/pulse-reports/${id}/stats?period=${period}`);
};

/**
 * Get send history
 */
export const getSendHistory = async (
  id: string,
  page: number = 1,
  limit: number = 20
): Promise<{ status: string; data: any[]; pagination: PaginationData }> => {
  return api.get(`/pulse-reports/${id}/history?page=${page}&limit=${limit}`);
};

/**
 * Get recipients
 */
export const getRecipients = async (
  id: string,
  page: number = 1,
  limit: number = 20,
  status?: string,
  search?: string,
  opened?: boolean
): Promise<{ status: string; data: any[]; pagination: PaginationData }> => {
  const params = new URLSearchParams({
    page: String(page),
    limit: String(limit),
  });

  if (status) params.append("status", status);
  if (search) params.append("search", search);
  if (opened !== undefined) params.append("opened", String(opened));

  return api.get(`/pulse-reports/${id}/recipients?${params.toString()}`);
};

/**
 * Get available template variables
 */
export const getAvailableVariables = async (): Promise<AvailableVariablesResponse> => {
  return api.get("/pulse-reports/variables");
};

/**
 * Get user pulse reports (generated reports)
 */
export const getUserPulseReports = async (
  page: number = 1,
  limit: number = 20,
  pulseReport?: string,
  user?: string,
  status?: string,
  dateFrom?: string,
  dateTo?: string
): Promise<{ status: string; data: any[]; pagination: PaginationData }> => {
  const params = new URLSearchParams({
    page: String(page),
    limit: String(limit),
  });

  if (pulseReport) params.append("pulseReport", pulseReport);
  if (user) params.append("user", user);
  if (status) params.append("status", status);
  if (dateFrom) params.append("dateFrom", dateFrom);
  if (dateTo) params.append("dateTo", dateTo);

  return api.get(`/pulse-reports/user-reports?${params.toString()}`);
};

/**
 * Get user pulse report by ID
 */
export const getUserPulseReportById = async (
  id: string
): Promise<{ status: string; data: any }> => {
  return api.get(`/pulse-reports/user-reports/${id}`);
};

/**
 * Get reports for a specific user
 */
export const getReportsForUser = async (
  userId: string,
  page: number = 1,
  limit: number = 20,
  reportType?: ReportType
): Promise<{ status: string; data: any[]; pagination: PaginationData }> => {
  const params = new URLSearchParams({
    page: String(page),
    limit: String(limit),
  });

  if (reportType) params.append("reportType", reportType);

  return api.get(`/pulse-reports/user-reports/user/${userId}?${params.toString()}`);
};

/**
 * Get pulse reports dashboard analytics
 */
export const getPulseReportsDashboardStats = async (
  period: string = "30d"
): Promise<{
  status: string;
  data: {
    overview: {
      totalReports: number;
      activeReports: number;
      totalSent: number;
      totalDelivered: number;
      totalOpened: number;
      totalClicked: number;
      totalFailed: number;
      totalBounced: number;
      openRate: number;
      clickRate: number;
      bounceRate: number;
    };
    recentActivity: Array<{
      reportName: string;
      sentAt: string;
      sent: number;
      delivered: number;
      failed: number;
    }>;
    reportStats: Array<{
      _id: string;
      name: string;
      status: string;
      totalSent: number;
      openRate: number;
      clickRate: number;
    }>;
    dailyStats: Array<{
      date: string;
      sent: number;
      delivered: number;
      opened: number;
      clicked: number;
    }>;
  };
}> => {
  return api.get(`/pulse-reports/dashboard-stats?period=${period}`);
};

// ============================================
// SENDING CONTROL API FUNCTIONS
// ============================================

/**
 * Pause a running pulse report (mid-send)
 * Saves checkpoint for later resumption
 */
export const pauseRunningPulseReport = async (
  id: string
): Promise<{
  status: string;
  message: string;
  data: {
    status: string;
    checkpoint?: {
      index: number;
      processedCount: number;
      remainingCount: number;
    };
  };
}> => {
  return api.post(`/pulse-reports/${id}/pause-running`);
};

/**
 * Resume a pulse report from checkpoint
 * Continues sending from where it was paused
 */
export const resumeFromCheckpoint = async (
  id: string
): Promise<{
  status: string;
  message: string;
  data: {
    status: string;
    checkpoint?: {
      index: number;
      processedCount: number;
      remainingCount: number;
    };
  };
}> => {
  return api.post(`/pulse-reports/${id}/resume-checkpoint`);
};

/**
 * Stop a pulse report permanently
 * Cannot be resumed after stopping
 */
export const stopPulseReport = async (
  id: string
): Promise<{
  status: string;
  message: string;
  data: {
    status: string;
    finalStats: {
      sent: number;
      failed: number;
    };
  };
}> => {
  return api.post(`/pulse-reports/${id}/stop`);
};

/**
 * Get real-time progress of a sending pulse report
 */
export const getPulseReportProgress = async (
  id: string
): Promise<{
  status: string;
  data: PulseReportProgress;
}> => {
  return api.get(`/pulse-reports/${id}/progress`);
};
